import {
  DueDisplayOptions,
  GroupByMode,
  GroupSetting,
  ID,
  newTaskSuffix,
  overdueSectionID,
  PrecomputedCompletion,
  RelationshipUpdate,
  Task,
  Timestamp,
  UpdateTask,
  WeekDays,
} from '@griply/types';
import {
  getNewTaskPrefill,
  getOverdueTasksFromTree,
  getTaskIdFromTreeId,
  onDeadlineReorder,
  parseDateKey,
} from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TaskListItemTypes,
  TreeItem,
  useTreeValue,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';

import { DeadlineHeader } from '../deadline-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './deadline-grouping-tasks-list.style';

const taskItemTypes = [TaskListItemTypes.Task];

export type MultipleDeadlinesGroupTaskListProps = {
  treeData: Record<ID, TreeItem>;
  tasks: Task[];
  onReorder?: (
    updatedSections: { id: ID; taskSorting: ID[] }[],
    updatedTask: UpdateTask[],
    relationshipUpdates?: RelationshipUpdate[],
  ) => void;
  weekStartsOn: WeekDays;
  collapsedSections?: ID[];
  onSectionCollapse?: (sectionIds: ID[]) => void;
  scrollElement?: React.RefObject<HTMLDivElement>;
  denseFormat?: boolean;
  isContextMenuOpen?: boolean;
  defaultNewTaskDate?: Timestamp;
  goalId?: ID;
  lifeAreaId?: ID;
  tagId?: ID;
  displayDue?: DueDisplayOptions[];
  showRepeatingAsTodo?: boolean;
  completionData?: Record<string, PrecomputedCompletion>;
};

export const MultipleDeadlinesGroupTaskList: React.FC<
  MultipleDeadlinesGroupTaskListProps
> = ({
  treeData,
  tasks,
  onReorder,
  weekStartsOn,
  collapsedSections = [],
  onSectionCollapse,
  scrollElement,
  denseFormat,
  isContextMenuOpen,
  defaultNewTaskDate,
  goalId,
  lifeAreaId,
  tagId,
  displayDue,
  showRepeatingAsTodo,
  completionData,
}) => {
  const today = useToday();
  const { submit: updateTasks } = useUpdateTasks();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  // Only clone treeData when drag-reorder is meaningful (not on the
  // completed page where completionData is provided).
  const oldTreeData = useMemo(
    () => (completionData ? treeData : structuredClone(treeData)),
    [treeData, completionData],
  );

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onDeadlineReorder({
      updatedItems,
      oldItems: Object.values(oldTreeData),
      tasksMap,
      today,
      weekStartsOn,
    });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const completionDataRef = useRef(completionData);
  completionDataRef.current = completionData;

  const overdueTasks = useMemo(
    () => getOverdueTasksFromTree(treeData, tasksMap),
    [treeData, tasksMap],
  );
  const overdueTasksRef = useRef(overdueTasks);
  overdueTasksRef.current = overdueTasks;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TaskListItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;
          const parentElement = itemInstance.getParent();
          const parentId = parentElement?.getItemData().sourceId;
          const date = parentId ? parseDateKey(parentId) : undefined;
          const sectionDate = date && !isNaN(date.getTime()) ? date : undefined;

          // Hide due date unless it is the overdue section
          const effectiveDisplayDue =
            parentId === overdueSectionID ? displayDue : [];

          const taskId = getTaskIdFromTreeId(item.sourceId);

          return currentTasksMap[taskId] ? (
            <TreeTaskCard
              task={currentTasksMap[taskId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              completionDate={sectionDate}
              displayDate={sectionDate}
              displayDue={effectiveDisplayDue}
              showRepeatingAsTodo={showRepeatingAsTodo}
              precomputedCompletion={completionDataRef.current?.[item.sourceId]}
            />
          ) : null;
        }

        // New Task
        if (type === TaskListItemTypes.NewTask) {
          const groupSectionId = item.sourceId.replace(newTaskSuffix, '');
          const groupSetting: GroupSetting = { goalId, lifeAreaId, tagId };
          const prefill = getNewTaskPrefill({
            groupByMode: GroupByMode.Deadline,
            groupSectionId,
            groupSetting,
            today,
            defaultNewTaskDate,
          });
          return (
            <NewTaskRow
              date={prefill.startDate}
              startTime={null}
              duration={null}
              goal={prefill.goalId}
              lifeArea={prefill.lifeAreaId}
              tags={prefill.tagIds}
            />
          );
        }

        // Date
        if (type === TaskListItemTypes.Date) {
          return (
            <DeadlineHeader
              denseFormat={denseFormat}
              item={itemInstance}
              overdueTasks={overdueTasksRef.current}
            />
          );
        }

        return null;
      },
    [
      denseFormat,
      today,
      defaultNewTaskDate,
      goalId,
      lifeAreaId,
      tagId,
      displayDue,
      showRepeatingAsTodo,
    ],
  );

  const tree = useTreeValue();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskItemTypes}
        dragTypes={taskItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
